import { FC } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";
import Table from "react-bootstrap/Table";
import Badge from "react-bootstrap/Badge";
import { FaArrowDown, FaRedo, FaTrash, FaUserPlus } from "react-icons/fa";
import { mapNodes } from "app/util/graph";
import { formatISODateTime } from "app/util/format";
import { NavigationScreen } from "app/component/screen/NavigationScreen";
import { LoadingScreen } from "app/component/screen/LoadingScreen";
import { ErrorScreen } from "app/component/screen/ErrorScreen";
import { ReportInviteListFieldsFragment, useReportInvitesListQuery } from "app/api/graph/types";
import {
    createReportInviteRoute,
    deleteReportInviteRoute,
    reportInvitesRoute,
    resendReportInviteRoute,
} from "app/route/Routes";
import { useRouteParams } from "app/route/route";
import { reportGraphId } from "app/api/graph/helpers";
import { RouteBreadcrumbs } from "app/route/RouteBreadcrumbs";
import { Toolbar, ToolbarButtons, ToolbarTitle } from "app/component/util/Toolbar";
import { reportRoleLabel } from "app/api/graph/strings";
import { ButtonLink } from "app/component/button/ButtonLink";
import { useGeneralPermissions } from "app/api/graph/hook/use-general-permissions";
import { useReportIdPermissions } from "app/api/graph/hook/use-report-permissions";

const whoLabel = (invite: ReportInviteListFieldsFragment) => {
    if (invite.acceptedByFullName) {
        return invite.acceptedByFullName;
    }

    const name = [invite.firstName, invite.lastName].filter((n) => n?.length ?? 0 > 0).join(" ");
    if (name.length > 0) {
        return invite.email ? `${name} (${invite.email})` : name;
    } else if (invite.email) {
        return invite.email;
    }

    return "Unknown";
};

type TableRowProps = {
    reportId: number;
    invite: ReportInviteListFieldsFragment;
};

const TableRow = ({ reportId, invite }: TableRowProps) => {
    const permissions = useGeneralPermissions(invite.permissions);

    return (
        <tr key={invite.id}>
            <td>{whoLabel(invite)}</td>
            <td>{reportRoleLabel(invite.role)}</td>
            <td>{formatISODateTime(invite.createdAt)}</td>
            <td>{invite.createdByFullName ?? "-"}</td>
            <td>{formatISODateTime(invite.expiresAt)}</td>
            <td>
                {invite.accepted ? (
                    <Badge bg="success">Accepted</Badge>
                ) : invite.expired ? (
                    <Badge bg="danger">Expired</Badge>
                ) : (
                    <Badge bg="info">Pending</Badge>
                )}
            </td>
            <td>
                {!invite.accepted && permissions.canUpdate ? (
                    <ButtonLink
                        to={resendReportInviteRoute.build({
                            reportId,
                            inviteId: invite._id,
                        })}
                        variant="warning"
                        size="sm"
                        title="Resend"
                    >
                        <FaRedo />
                    </ButtonLink>
                ) : null}
                {permissions.canDelete ? (
                    <ButtonLink
                        to={deleteReportInviteRoute.build({
                            reportId,
                            inviteId: invite._id,
                        })}
                        variant="danger"
                        size="sm"
                        title="Delete"
                    >
                        <FaTrash />
                    </ButtonLink>
                ) : null}
            </td>
        </tr>
    );
};

export const ReportInvitesListScreen: FC = () => {
    const { reportId } = useRouteParams(reportInvitesRoute);
    const { data, loading, error, fetchMore } = useReportInvitesListQuery({
        notifyOnNetworkStatusChange: true,
        variables: { reportId: reportGraphId(reportId) },
    });
    const invites = data?.report?.invites;
    const permissions = useReportIdPermissions(reportGraphId(reportId));

    if (!invites && loading) {
        return <LoadingScreen />;
    }
    if (error) {
        return <ErrorScreen title="Error" message={error.message} />;
    }
    if (!invites) {
        return <ErrorScreen title="Error" message="Not found." />;
    }

    const pageInfo = invites.pageInfo;
    return (
        <NavigationScreen>
            <Container>
                <RouteBreadcrumbs route={reportInvitesRoute} />
                <Toolbar>
                    <ToolbarTitle>
                        <h1>Invites</h1>
                    </ToolbarTitle>
                    <ToolbarButtons>
                        {permissions.canCreateUserInvite ? (
                            <ButtonLink to={createReportInviteRoute.build({ reportId })} variant="success">
                                <FaUserPlus /> Invite
                            </ButtonLink>
                        ) : null}
                    </ToolbarButtons>
                </Toolbar>
                <Table responsive="sm" bordered className="crud-table">
                    <thead>
                        <tr>
                            <th>Who</th>
                            <th>Role</th>
                            <th>Invited at</th>
                            <th>Invited by</th>
                            <th>Expires at</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {mapNodes(invites, (invite) => (
                            <TableRow key={invite.id} reportId={reportId} invite={invite} />
                        ))}
                    </tbody>
                </Table>
                {invites.edges?.length === 0 ? (
                    <Alert variant="info">There are no invites for this report.</Alert>
                ) : null}
                {pageInfo?.hasNextPage ? (
                    <Button
                        onClick={() => fetchMore({ variables: { after: pageInfo.endCursor } })}
                        disabled={loading}
                        variant="light"
                    >
                        <FaArrowDown /> Load more
                    </Button>
                ) : null}
            </Container>
        </NavigationScreen>
    );
};
